import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { FocusUtils } from "abreact/incubator/ui/utils/FocusUtils";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbCalendar } from "abreact/input/date/AbCalendar";
import { AbTimeInput, AbTimePrecision } from "abreact/input/date/AbTimeInput";
import { AbHSeparator } from "abreact/layout/AbHSeparator";
import { AbIcon } from "abreact/misc/AbIcon";
import { AbPopupButton } from "abreact/misc/AbPopupButton";
import * as AbHooks from "abreact/utils/AbHooks";
import { DateUtils } from "abweb/utils/DateUtils";
import { NumberUtils } from "abweb/utils/NumberUtils";
import { AbCalendarDay } from "abweb/utils/date/AbCalendarDay";
import { AbTime } from "abweb/utils/date/AbTime";
import { DateTimeUtils } from "abweb/utils/date/DateTimeUtils";
import * as React from "react";
const clampDate = (date, earliest, latest) => {
  const clampedEpoch = NumberUtils.clamp(date.getTime(), earliest.getTime(), latest.getTime());
  return new Date(clampedEpoch);
};
const merge = (day, time) => {
  const d = DateUtils.newLocalDateWithFullYear(day.year, day.month - 1, day.day);
  return DateUtils.partialOverrideDate(d, time);
};
const calcDefaultTimeString = (defaultTime, precision) => {
  const formatter = precision === AbTimePrecision.SECONDS ? DateTimeUtils.TIME_WITH_SECONDS : DateTimeUtils.TIME_WITH_MINUTES;
  return formatter.format(AbTime.toDate(defaultTime));
};
const AbDateTimeInputPopup = props => {
  const [dayValue, setDayValue] = React.useState(props.value ? AbCalendarDay.fromLocalDate(props.value) : props.partialValue.current.day);
  const [timeValue, setTimeValue] = React.useState(props.value ? AbTime.fromDate(props.value) : props.partialValue.current.time);
  const timeWasDefaulted = React.useRef(false);
  const timeRef = React.useRef(null);
  const earliestDay = AbCalendarDay.fromLocalDate(props.earliestDate);
  const latestDay = AbCalendarDay.fromLocalDate(props.latestDate);
  const calendarRef = React.useRef(null);
  const shouldHideTimeValue = timeWasDefaulted.current && props.defaultTime;
  const onChange = newDate => {
    if (newDate && (props.earliestDate > newDate || props.latestDate < newDate)) return;
    props.onChange(newDate);
    const timeInputEmpty = timeRef.current?.inputElement.value === "";
    timeWasDefaulted.current = Boolean(timeInputEmpty && props.defaultTime);
  };
  AbHooks.useOnValuesChange(() => {
    if (!props.value) {
      setDayValue(undefined);
      setTimeValue(undefined);
      return;
    }
    const day = AbCalendarDay.fromLocalDate(props.value);
    const time = AbTime.fromDate(props.value);
    setDayValue(day);
    setTimeValue(time);
  }, [props.value?.getTime(), props.earliestDate.getTime(), props.latestDate.getTime()]);
  const onDayChange = newDay => {
    setDayValue(newDay);
    props.partialValue.current.day = newDay;
    if (!props.withTime) {
      onChange(newDay.localMidnightDate);
      return;
    }
    const timeInputEmpty = timeRef.current?.inputElement.value === "";
    const defaultedTime = timeValue ?? (timeInputEmpty ? props.defaultTime : undefined);
    if (defaultedTime) {
      const clampedDate = clampDate(merge(newDay, defaultedTime), props.earliestDate, props.latestDate);
      onChange(clampedDate);
    } else {
      FocusUtils.setFocus(timeRef.current?.inputElement);
    }
  };
  const onTimeChange = newTime => {
    setTimeValue(newTime);
    props.partialValue.current.time = newTime;
    const timeInputEmpty = timeRef.current?.inputElement.value === "";
    const defaultedNewTime = newTime ?? (timeInputEmpty ? props.defaultTime : undefined);
    if (!defaultedNewTime) {
      if (!dayValue) onChange(undefined);
      return;
    }
    if (dayValue) {
      onChange(merge(dayValue, defaultedNewTime));
      return;
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AbCalendar, {
    onChange: onDayChange,
    value: dayValue,
    abRef: ReactUtils.mergeRefs(calendarRef, props.abRef),
    earliestDay: earliestDay,
    latestDay: latestDay
  }), props.withTime && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AbHSeparator, null), /*#__PURE__*/React.createElement("div", {
    className: "ab-date-input-time-container"
  }, /*#__PURE__*/React.createElement(AbIcon, {
    image: "absdk:time"
  }), /*#__PURE__*/React.createElement(AbTimeInput, {
    value: shouldHideTimeValue ? undefined : props.value ? AbTime.fromDate(props.value) : props.partialValue.current.time,
    onChange: onTimeChange,
    textInputProps: {
      onChange: v => {
        if (v !== "") return;
        if (props.defaultTime) onTimeChange(undefined);
      }
    },
    precision: props.timePrecision,
    placeholder: props.defaultTime ? calcDefaultTimeString(props.defaultTime, props.timePrecision) : undefined,
    earliestTime: dayValue && earliestDay.equals(dayValue) ? AbTime.fromDate(props.earliestDate) : undefined,
    latestTime: dayValue && latestDay.equals(dayValue) ? AbTime.fromDate(props.latestDate) : undefined,
    abRef: timeRef
  }))));
};
export const AbDateTimeInputPopupButton = props => {
  const popupButtonRef = React.useRef(null);
  const popupRef = React.useRef(null);
  const partialValueRef = React.useRef({});
  AbHooks.useOnValuesChange(() => {
    if (!props.value) {
      partialValueRef.current = {};
      return;
    }
    const day = AbCalendarDay.fromLocalDate(props.value);
    const time = AbTime.fromDate(props.value);
    partialValueRef.current = {
      day: day,
      time: time
    };
  }, [props.value?.getTime(), props.earliestDate.getTime(), props.latestDate.getTime()]);
  const onChange = props.withTime ? props.onChange : date => {
    if (date?.getTime() !== props.value?.getTime()) props.onChange(date);
    popupButtonRef.current?.close();
  };
  const popupProps = {
    className: "ab-date-input-popup",
    setFocus: () => popupRef.current?.buttonElement.focus(),
    get origin() {
      return props.textInputRef.current?.inputElement ?? undefined;
    }
  };
  return /*#__PURE__*/React.createElement(AbPopupButton, _extends({}, ReactUtils.componentProps("ab-date-time-input-popup-button", props, ["ab-no-border-button", "ab-date-input-calendar-button"]), {
    abRef: popupButtonRef,
    icon: props.withTime ? "absdk:date-time" : "absdk:calendar",
    disabled: props.disabled,
    tabIndex: props.tabIndex,
    popupProps: popupProps,
    popupContents: /*#__PURE__*/React.createElement(AbDateTimeInputPopup, {
      onChange: onChange,
      value: props.value,
      withTime: props.withTime,
      timePrecision: props.timePrecision,
      defaultTime: props.defaultTime,
      earliestDate: props.earliestDate,
      latestDate: props.latestDate,
      partialValue: partialValueRef,
      abRef: popupRef
    })
  }));
};
//# sourceMappingURL=AbDateTimeInputPopupButton.js.map
